import { useEffect } from "react";
import { useSnapshot } from "valtio";
import {
  organizationContractsStore,
  resetOrganizationContractsStore,
} from "../organizationContractsStore.ts";
import { ContractService } from "@services/finance/contracts/contractService";
import { BudgetService } from "@services/finance/budgets/budgetService";
import { PricesheetService } from "@services/finance/pricesheets/pricesheetService";
import { SpendAccountService } from "@services/finance/spendAccounts/spendAccountService";
import { CreditService } from "@services/finance/credits/creditService";
import { DebitService } from "@services/finance/debits/debitService";
import { CreateCreditPayload } from "@services/finance/credits/commands/cmdCreateCredit";
import { UpdateCreditPayload } from "@services/finance/credits/commands/cmdUpdateCredit";
import { CreateDebitPayload } from "@services/finance/debits/commands/cmdCreateDebit";
import { UpdateDebitPayload } from "@services/finance/debits/commands/cmdUpdateDebit";
import { CreateContractPayload } from "@services/finance/contracts/commands/cmdCreateContract";
import { UpdateContractPayload } from "@services/finance/contracts/commands/cmdUpdateContract";
import { CreateBudgetPayload } from "@services/finance/budgets/commands/cmdCreateBudget";
import { UpdateBudgetPayload } from "@services/finance/budgets/commands/cmdUpdateBudget";
import { CreatePricesheetPayload } from "@services/finance/pricesheets/commands/cmdCreatePricesheet";
import { UpdatePricesheetPayload } from "@services/finance/pricesheets/commands/cmdUpdatePricesheet";
import { ItemChanges, PathChanges } from "src/modules/contracts/components/modals/PricesheetFormModal";
import { LineItemRow } from "src/modules/contracts/components/pricesheet/PriceSheetLineItem";
import showAlert from "src/shared/utils/generic.tsx";

function toItemPayload(row: LineItemRow, pathIdMap: Map<number, number>) {
  let pathId = row.journey_path_id;
  if (pathId !== null && pathId < 0) {
    const resolved = pathIdMap.get(pathId);
    pathId = resolved ?? null;
  }
  return {
    name: row.name,
    journey_step_id: row.journey_step_id,
    journey_path_id: pathId,
    step_price: row.step_price,
    estimated_roi: row.estimated_roi,
    unit_price: row.unit_price,
    sequence: row.sequence,
    conversion_rule_text: row.conversion_rule_text,
    description: row.description ?? "",
    is_mandatory: row.is_mandatory ?? false,
    has_dependency: row.has_dependency ?? false,
  };
}

const contractService = new ContractService();
const budgetService = new BudgetService();
const pricesheetService = new PricesheetService();
const spendAccountService = new SpendAccountService();
const creditService = new CreditService();
const debitService = new DebitService();

const useOrganizationContracts = (organizationId: number) => {
  const snap = useSnapshot(organizationContractsStore);

  useEffect(() => {
    if (!organizationId) return;
    if (organizationId !== organizationContractsStore.organizationId) {
      resetOrganizationContractsStore();
      organizationContractsStore.organizationId = organizationId;
    }
    loadContracts(organizationId);
  }, [organizationId]);

  async function loadContracts(orgId: number) {
    organizationContractsStore.isLoading = true;
    organizationContractsStore.error = null;
    try {
      const contracts = await contractService.list({ client_org_id: orgId });
      organizationContractsStore.contracts = contracts;

      const currentId = organizationContractsStore.selectedContractId;
      const stillExists = currentId != null && contracts.some((c) => c.id === currentId);

      if (stillExists) {
        await selectContract(currentId!);
      } else if (contracts.length > 0) {
        await selectContract(contracts[0].id);
      } else {
        organizationContractsStore.selectedContractId = null;
        organizationContractsStore.budgets = [];
        organizationContractsStore.pricesheets = [];
        organizationContractsStore.pricesheetItems = [];
        organizationContractsStore.spendAccount = null;
        organizationContractsStore.credits = [];
        organizationContractsStore.debits = [];
        organizationContractsStore.balance = null;
      }
    } catch {
      organizationContractsStore.error = "Failed to load contracts.";
    } finally {
      organizationContractsStore.isLoading = false;
    }
  }

  async function selectContract(contractId: number) {
    organizationContractsStore.selectedContractId = contractId;
    organizationContractsStore.isLoading = true;
    try {
      const [budgets, pricesheets] = await Promise.all([
        budgetService.list({ contract_id: contractId }),
        pricesheetService.list(contractId),
      ]);
      organizationContractsStore.budgets = budgets;
      organizationContractsStore.pricesheets = pricesheets;

      const allItems = await Promise.all(
        pricesheets.map((ps) => pricesheetService.listItems(contractId, ps.id)),
      );
      organizationContractsStore.pricesheetItems = allItems.flat();

      await loadSpend(contractId);
    } catch {
      organizationContractsStore.error = "Failed to load contract details.";
    } finally {
      organizationContractsStore.isLoading = false;
    }
  }

  // Spend account, ledger, and authoritative balance. Balance recomputes lazily
  // on the backend after budget/credit/debit changes, so refetch it alongside.
  async function loadSpend(contractId: number) {
    const [spendAccount, credits, debits, balance] = await Promise.all([
      spendAccountService.get(contractId),
      creditService.list(contractId),
      debitService.list(contractId),
      spendAccountService.getBalance(contractId),
    ]);
    organizationContractsStore.spendAccount = spendAccount;
    organizationContractsStore.credits = credits;
    organizationContractsStore.debits = debits;
    organizationContractsStore.balance = balance ? balance.balance : null;
  }

  async function reloadSelected() {
    if (organizationContractsStore.selectedContractId) {
      await selectContract(organizationContractsStore.selectedContractId);
    }
  }

  async function reloadSpend() {
    if (organizationContractsStore.selectedContractId) {
      await loadSpend(organizationContractsStore.selectedContractId);
    }
  }

  async function createContract(payload: CreateContractPayload) {
    organizationContractsStore.isSaving = true;
    try {
      const contract = await contractService.create(payload);
      organizationContractsStore.selectedContractId = contract.id;
      await loadContracts(organizationId);
      return contract;
    } catch (err) {
      console.error("Failed to create contract:", err);
      showAlert("Failed to create contract", "error", 3000);
      throw err;
    } finally {
      organizationContractsStore.isSaving = false;
    }
  }

  async function updateContract(contractId: number, payload: UpdateContractPayload) {
    organizationContractsStore.isSaving = true;
    try {
      await contractService.update(contractId, payload);
      await loadContracts(organizationId);
    } catch (err) {
      showAlert("Failed to update contract", "error", 3000);
      throw err;
    } finally {
      organizationContractsStore.isSaving = false;
    }
  }

  async function deleteContract(contractId: number) {
    organizationContractsStore.isSaving = true;
    try {
      await contractService.delete(contractId);
      organizationContractsStore.selectedContractId = null;
      await loadContracts(organizationId);
    } catch (err) {
      showAlert("Failed to delete contract", "error", 3000);
      throw err;
    } finally {
      organizationContractsStore.isSaving = false;
    }
  }

  async function createBudget(payload: CreateBudgetPayload) {
    organizationContractsStore.isSaving = true;
    try {
      await budgetService.create(payload);
      await reloadSelected();
    } catch (err) {
      showAlert("Failed to create budget", "error", 3000);
      throw err;
    } finally {
      organizationContractsStore.isSaving = false;
    }
  }

  async function updateBudget(budgetId: number, payload: UpdateBudgetPayload) {
    organizationContractsStore.isSaving = true;
    try {
      await budgetService.update(budgetId, payload);
      await reloadSelected();
    } catch (err) {
      showAlert("Failed to update budget", "error", 3000);
      throw err;
    } finally {
      organizationContractsStore.isSaving = false;
    }
  }

  async function deleteBudget(budgetId: number) {
    organizationContractsStore.isSaving = true;
    try {
      await budgetService.delete(budgetId);
      await reloadSelected();
    } catch (err) {
      showAlert("Failed to delete budget", "error", 3000);
      throw err;
    } finally {
      organizationContractsStore.isSaving = false;
    }
  }

  async function savePricesheet(
    pricesheetId: number | null,
    payload: CreatePricesheetPayload | UpdatePricesheetPayload,
    itemChanges: ItemChanges,
    pathChanges: PathChanges,
  ) {
    const contractId = organizationContractsStore.selectedContractId;
    if (contractId == null) return;
    organizationContractsStore.isSaving = true;
    try {
      let psId = pricesheetId;
      if (psId == null) {
        const ps = await pricesheetService.create(contractId, payload as CreatePricesheetPayload);
        psId = ps.id;
      } else {
        await pricesheetService.update(contractId, psId, payload as UpdatePricesheetPayload);
      }

      // Path changes first — delete (cascades to items on BE), then rename, then create.
      for (const id of pathChanges.deletedPathIds) {
        await pricesheetService.deletePath(contractId, psId, id);
      }
      for (const p of pathChanges.editedPaths) {
        await pricesheetService.updatePath(contractId, psId, p.id, {
          name: p.name,
          population_segment_id: p.populationSegmentId,
        });
      }
      const pathIdMap = new Map<number, number>();
      for (const p of pathChanges.newPaths) {
        const created = await pricesheetService.createPath(
          contractId, psId, p.name, p.populationSegmentId,
        );
        pathIdMap.set(p.id, created.id);
      }

      for (const id of itemChanges.deletedIds) {
        await pricesheetService.deleteItem(contractId, psId, id);
      }
      for (const item of itemChanges.editedItems) {
        await pricesheetService.updateItem(contractId, psId, item.id, toItemPayload(item, pathIdMap));
      }
      for (const item of itemChanges.newItems) {
        await pricesheetService.createItem(contractId, psId, toItemPayload(item, pathIdMap));
      }

      await reloadSelected();
    } catch (err) {
      // The server's 422 names the fix (duplicate step, population already priced).
      showAlert(err instanceof Error && err.message ? err.message : "Failed to save pricesheet", "error", 8000);
      throw err;
    } finally {
      organizationContractsStore.isSaving = false;
    }
  }

  async function deletePricesheet(pricesheetId: number) {
    const contractId = organizationContractsStore.selectedContractId;
    if (contractId == null) return;
    organizationContractsStore.isSaving = true;
    try {
      await pricesheetService.delete(contractId, pricesheetId);
      await reloadSelected();
    } catch (err) {
      showAlert("Failed to delete pricesheet", "error", 3000);
      throw err;
    } finally {
      organizationContractsStore.isSaving = false;
    }
  }

  async function withSaving(action: () => Promise<void>, failMsg: string) {
    organizationContractsStore.isSaving = true;
    try {
      await action();
      await reloadSpend();
    } catch (err) {
      showAlert(failMsg, "error", 3000);
      throw err;
    } finally {
      organizationContractsStore.isSaving = false;
    }
  }

  function createCredit(payload: CreateCreditPayload) {
    const contractId = organizationContractsStore.selectedContractId;
    if (contractId == null) return Promise.resolve();
    return withSaving(
      () => creditService.create(contractId, payload).then(() => undefined),
      "Failed to add credit",
    );
  }

  function updateCredit(creditId: number, payload: UpdateCreditPayload) {
    const contractId = organizationContractsStore.selectedContractId;
    if (contractId == null) return Promise.resolve();
    return withSaving(
      () => creditService.update(contractId, creditId, payload).then(() => undefined),
      "Failed to update credit",
    );
  }

  function deleteCredit(creditId: number) {
    const contractId = organizationContractsStore.selectedContractId;
    if (contractId == null) return Promise.resolve();
    return withSaving(
      () => creditService.delete(contractId, creditId).then(() => undefined),
      "Failed to delete credit",
    );
  }

  function createDebit(payload: CreateDebitPayload) {
    const contractId = organizationContractsStore.selectedContractId;
    if (contractId == null) return Promise.resolve();
    return withSaving(
      () => debitService.create(contractId, payload).then(() => undefined),
      "Failed to add debit",
    );
  }

  function updateDebit(debitId: number, payload: UpdateDebitPayload) {
    const contractId = organizationContractsStore.selectedContractId;
    if (contractId == null) return Promise.resolve();
    return withSaving(
      () => debitService.update(contractId, debitId, payload).then(() => undefined),
      "Failed to update debit",
    );
  }

  function deleteDebit(debitId: number) {
    const contractId = organizationContractsStore.selectedContractId;
    if (contractId == null) return Promise.resolve();
    return withSaving(
      () => debitService.delete(contractId, debitId).then(() => undefined),
      "Failed to delete debit",
    );
  }

  async function selectAndLoad(contractId: number) {
    await selectContract(contractId);
  }

  return {
    contracts: snap.contracts,
    selectedContractId: snap.selectedContractId,
    budgets: snap.budgets,
    pricesheets: snap.pricesheets,
    pricesheetItems: snap.pricesheetItems,
    spendAccount: snap.spendAccount,
    credits: snap.credits,
    debits: snap.debits,
    balance: snap.balance,
    isLoading: snap.isLoading,
    isSaving: snap.isSaving,
    error: snap.error,
    selectContract: selectAndLoad,
    createContract,
    updateContract,
    deleteContract,
    createBudget,
    updateBudget,
    deleteBudget,
    savePricesheet,
    deletePricesheet,
    createCredit,
    updateCredit,
    deleteCredit,
    createDebit,
    updateDebit,
    deleteDebit,
  };
};

export default useOrganizationContracts;
